import { Injectable } from '@angular/core';
import { Logger } from '@common/logging/logger';
import { FeathersService } from '@services/feathers.service';
import { Observable, of } from 'rxjs';
import { v4 } from 'uuid';
import { AttachmentDto } from '../../models/attachment/attachment-dto.model';
import { arrayBufferToBlob, prepareDataForUpload } from './attachment-helper';
import { IAttachmentTransferService } from './attachment-transfer-service.interface';
import { OfflineUploadResult, UploadData, UploadResult } from './attachment-transfer.service';

/**
 * Main purpose of this class is the circumvent Attachment Queues and Attachment-Database processing for web
 */
@Injectable({ providedIn: 'root' })
export class WebAttachmentTransferService implements IAttachmentTransferService {
  constructor(
    private readonly _logger: Logger,
    private _feathersService: FeathersService
  ) {}
  public get offlineTxSuccess(): Observable<OfflineUploadResult> {
    // Return an empty observable to avoid breaking dependent code
    return of();
  }

  public async upload(
    data: UploadData,
    localId: string = v4(),
    tryUploadingImmediately: boolean = true,
    isFromQueue?: boolean
  ): Promise<UploadResult> {
    this._logger.info(`upload to backend ${localId}`);
    const dataToSend = await prepareDataForUpload(data);
    const result = await this._feathersService.getService<AttachmentDto>('attachment').create(dataToSend);
    return { loading: false, data: { id: result._id } };
  }

  /**
   * Downloads a binary straight from the server (feathers `attachment.get`, ArrayBuffer).
   * Unlike the native `AttachmentTransferService.fetchBlob` there is no local store involved —
   * caching is the caller's concern (`WebAttachmentService.download` puts the result into the
   * IndexedDB LRU cache). Rejects when offline or the attachment does not exist on the server.
   */
  public async fetchBlob(id: string, mimeType: string): Promise<Blob> {
    const result = await this._feathersService.getService<ArrayBuffer>('attachment').get(id);
    const blob = arrayBufferToBlob(result, mimeType);
    return blob;
  }

  public async delete(id: string): Promise<void> {
    await this._feathersService.getService<AttachmentDto>('attachment').remove(id);
  }

  public async update(id: string, data: any): Promise<any> {
    const dataToSend = await prepareDataForUpload(data);
    return this._feathersService.getService<Partial<AttachmentDto>>('attachment').update(id, dataToSend);
  }
}
